import React, { useEffect, useState } from 'react';
import { format, addDays } from 'date-fns';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Modal, Button, Input } from '../../../../components/common';
import apiClient from '../../../../services/api';

interface Props {
  cycle: { id: string; name: string } | null;
  onDone: () => void;
  onCancel: () => void;
}

interface ReopenPreview {
  okrsToReopen: number;
  reopenableUntil: string | null;
  canReopen: boolean;
  reason?: string;
}

const DATE_INPUT_FORMAT = 'yyyy-MM-dd';
const toDateInput = (d: Date) => format(d, DATE_INPUT_FORMAT);

/** The reopen picker for a closed cycle. Fetches the preview itself; posts the reopen. Spec §5. */
export const ReopenCycleModal: React.FC<Props> = ({ cycle, onDone, onCancel }) => {
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cycleId = cycle?.id;

  // Reset to today + 14 (viewer's local date) whenever a new cycle is opened for reopen.
  useEffect(() => {
    if (cycleId) {
      setEndDate(toDateInput(addDays(new Date(), 14)));
      setError(null);
    }
  }, [cycleId]);

  const { data: preview, isLoading } = useQuery({
    queryKey: ['cycle-reopen-preview', cycleId],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: ReopenPreview }>(
        `/cycles/${cycleId}/reopen-preview`
      );
      return response.data.data;
    },
    enabled: !!cycleId,
  });

  const reopen = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(`/cycles/${cycleId}/reopen`, { endDate });
      return response.data;
    },
    onSuccess: () => {
      onDone();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message ?? 'Failed to reopen cycle.');
    },
  });

  if (!cycle) return null;

  const minDate = toDateInput(addDays(new Date(), 1));

  return (
    <Modal isOpen onClose={onCancel} title={`Reopen "${cycle.name}"?`}>
      <p className="text-sm text-stone-700">
        {preview ? `${preview.okrsToReopen} OKRs will open again with their old status.` : ''}
      </p>
      <p className="text-sm text-stone-700 mt-1">Owners with active accounts get a message in the app and by email.</p>
      <label className="block mt-4 text-sm text-stone-700" htmlFor="reopen-cycle-end-date">
        New end date
      </label>
      <Input
        id="reopen-cycle-end-date"
        type="date"
        value={endDate}
        min={minDate}
        onChange={(e) => setEndDate(e.target.value)}
      />
      {error && <p className="mt-2 text-sm text-danger-600">{error}</p>}
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button
          variant="primary"
          disabled={isLoading || reopen.isPending || !endDate}
          onClick={() => {
            setError(null);
            reopen.mutate();
          }}
        >
          Reopen cycle
        </Button>
      </div>
    </Modal>
  );
};
